import { API, TestCallerCandidate, TestCallerStatusResponse } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { TestCallerType } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, ButtonVariant, Spinner } from "@swivl-mono/ui";
import { useEffect, useState } from "react";

import { LocalStorageHelper } from "../../../Models/LocalStorage/LocalStorageHelper";
import { Member } from "../../../Models/Member/Member.Current";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";

const PHONE_STORAGE_KEY = "swivl-test-caller-phone";

/** UI picker entries — the three account filters plus the blank "unknown caller" (a reset, not a search). */
type PickerType = TestCallerType | "unknown";

const TYPE_OPTIONS: { type: PickerType; label: string; description: string }[] = [
  { type: "unknown", label: "Unknown caller", description: "A brand-new caller with nothing in the system" },
  { type: TestCallerType.Tenant, label: "Tenant — one lease", description: "Accounts with exactly one active lease" },
  { type: TestCallerType.MultiLease, label: "Tenant — multiple leases", description: "Accounts with two or more active leases" },
  { type: TestCallerType.PastDue, label: "Past-due tenant", description: "Accounts with a past-due active lease" }
];

/**
 * Test Caller (ENG-2866): attach a tester's phone number to a REAL account on
 * the current org so calling/texting the bot behaves as that account.
 * "Unknown caller" = Detach (the phone resolves to no account).
 */
export function TestCallerAdminView(): React.ReactElement {
  const orgId = Member.orgId;
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState<TestCallerStatusResponse | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [selectedType, setSelectedType] = useState<PickerType | null>(null);
  const [candidates, setCandidates] = useState<TestCallerCandidate[] | null>(null);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [busyUserId, setBusyUserId] = useState<number | null>(null);
  const [isDetaching, setIsDetaching] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [pendingCandidate, setPendingCandidate] = useState<TestCallerCandidate | null>(null);
  const [pendingUnknown, setPendingUnknown] = useState(false);
  const [overrideName, setOverrideName] = useState("");
  const [overrideEmail, setOverrideEmail] = useState("");

  const checkStatus = async (rawPhone: string): Promise<void> => {
    if (!orgId || !rawPhone.trim()) return;
    setIsLoadingStatus(true);
    try {
      const result = await API.TestCaller.getStatus(orgId, rawPhone.trim());
      setStatus(result);
      setPhone(result.phone);
      LocalStorageHelper.setItem(PHONE_STORAGE_KEY, result.phone);
    } catch (e) {
      AlertsModel.errorToast(e instanceof Error ? e.message : "Couldn't load status for that phone");
      setStatus(null);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  // Remember the last phone used: prefill AND auto-check it, so returning
  // users land with the picker already unlocked.
  useEffect(() => {
    const saved = LocalStorageHelper.getItem(PHONE_STORAGE_KEY);
    if (saved) {
      setPhone(saved);
      void checkStatus(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckStatus = async (): Promise<void> => {
    await checkStatus(phone);
  };

  // Editing the number invalidates the checked state — the picker locks
  // again until the new number is checked.
  const handlePhoneChange = (value: string): void => {
    setPhone(value);
    if (status && value.trim() !== status.phone) {
      setStatus(null);
      setSelectedType(null);
      setCandidates(null);
    }
  };

  const handleSelectType = async (type: PickerType): Promise<void> => {
    if (!orgId) return;
    if (!phone.trim()) {
      AlertsModel.errorToast("Enter your phone number first");
      return;
    }
    setSelectedType(type);

    // "Unknown caller" is a reset, not an account search — straight to the
    // confirm screen. Works regardless of what (if anything) is attached.
    if (type === "unknown") {
      setCandidates(null);
      setPendingUnknown(true);
      return;
    }

    setIsLoadingCandidates(true);
    try {
      const result = await API.TestCaller.getCandidates(orgId, type);
      setCandidates(result.candidates);
    } catch (e) {
      AlertsModel.errorToast(e instanceof Error ? e.message : "Couldn't load matching accounts");
      setCandidates(null);
    } finally {
      setIsLoadingCandidates(false);
    }
  };

  // Selecting a candidate opens the confirm screen with the override fields
  // prefilled to the ADMIN's own identity (most testers want to appear as
  // themselves on the account).
  const handleSelectCandidate = (candidate: TestCallerCandidate): void => {
    if (!phone.trim()) {
      AlertsModel.errorToast("Enter your phone number first");
      return;
    }
    setPendingCandidate(candidate);
    setOverrideName(Member.displayName || "");
    setOverrideEmail(Member.account?.email || "");
  };

  const handleConfirmApply = async (): Promise<void> => {
    if (!orgId || !phone.trim() || !pendingCandidate) return;
    setBusyUserId(pendingCandidate.userId);
    try {
      const override =
        overrideName.trim() || overrideEmail.trim()
          ? { name: overrideName.trim() || undefined, email: overrideEmail.trim() || undefined }
          : undefined;
      const result = await API.TestCaller.apply(orgId, phone.trim(), pendingCandidate.userId, override);
      setStatus(result.status);
      LocalStorageHelper.setItem(PHONE_STORAGE_KEY, result.status.phone);
      AlertsModel.successToast(result.message);
      setPendingCandidate(null);
    } catch (e) {
      AlertsModel.errorToast(e instanceof Error ? e.message : "Apply failed");
    } finally {
      setBusyUserId(null);
    }
  };

  const handleDetach = async (): Promise<void> => {
    if (!orgId || !phone.trim()) return;
    setIsDetaching(true);
    try {
      const result = await API.TestCaller.undo(orgId, phone.trim());
      setStatus(result.status);
      AlertsModel.successToast(result.message);
    } catch (e) {
      AlertsModel.errorToast(e instanceof Error ? e.message : "Detach failed");
    } finally {
      setIsDetaching(false);
    }
  };

  const handleConfirmUnknown = async (): Promise<void> => {
    if (!orgId || !phone.trim()) return;
    setIsResetting(true);
    try {
      const result = await API.TestCaller.reset(orgId, phone.trim());
      setStatus(result.status);
      LocalStorageHelper.setItem(PHONE_STORAGE_KEY, result.status.phone);
      AlertsModel.successToast(result.message);
      setPendingUnknown(false);
    } catch (e) {
      AlertsModel.errorToast(e instanceof Error ? e.message : "Reset failed");
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6 p-6">
      <div>
        <h2 className="text-lg font-semibold text-grey-800">Test Caller</h2>
        <p className="mt-1 text-sm text-grey-500">
          Attach your phone number to a real account on this org, so calling or texting the bot behaves as that account. Detach to become an unknown
          caller. Undo removes only the conversations and tickets created during your test window.
        </p>
      </div>

      {/* Phone input */}
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="mb-1 block text-xs font-medium text-grey-500" htmlFor="test-caller-phone">
            Your phone number
          </label>
          <input
            id="test-caller-phone"
            type="tel"
            className="w-full rounded-md border border-grey-200 px-3 py-2 text-sm text-grey-700 placeholder:text-grey-300 focus:border-orange-300 focus:outline-none"
            placeholder="+1 555 010 1234"
            value={phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleCheckStatus();
            }}
          />
        </div>
        <Button
          size={ButtonSize.SMALL}
          variant={ButtonVariant.PRIMARY}
          color={ButtonColor.ORANGE}
          onClick={handleCheckStatus}
          disabled={!phone.trim() || isLoadingStatus}
        >
          {isLoadingStatus ? <Spinner /> : "Check status"}
        </Button>
      </div>

      {/* Status card */}
      {status && (
        <div className="rounded-lg border border-grey-200 bg-white p-4">
          {!status.orgAllowed && (
            <p className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-600">Test Caller is not enabled for this org in this environment.</p>
          )}
          {status.activeMapping ? (
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-grey-800">
                  <span className="font-semibold">{status.phone}</span> is attached to{" "}
                  <span className="font-semibold">{status.activeMapping.userName || `user ${status.activeMapping.userId}`}</span>
                </p>
                <p className="mt-0.5 text-xs text-grey-500">
                  Applied {new Date(status.activeMapping.appliedAt).toLocaleString()}
                  {status.activeMapping.appliedByName ? ` by ${status.activeMapping.appliedByName}` : ""}
                </p>
                {(status.activeMapping.overrideName || status.activeMapping.overrideEmail) && (
                  <p className="mt-1 text-xs text-teal-600">
                    Identity override:{" "}
                    {status.activeMapping.overrideName && (
                      <span>
                        name "{status.activeMapping.overrideName}" (was "{status.activeMapping.originalName || "—"}")
                      </span>
                    )}
                    {status.activeMapping.overrideName && status.activeMapping.overrideEmail && <span> · </span>}
                    {status.activeMapping.overrideEmail && (
                      <span>
                        email "{status.activeMapping.overrideEmail}" (was "{status.activeMapping.originalEmail || "—"}")
                      </span>
                    )}
                  </p>
                )}
              </div>
              <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={handleDetach} disabled={isDetaching || isResetting}>
                {isDetaching ? <Spinner /> : "Undo (detach + clean up)"}
              </Button>
            </div>
          ) : status.carriers.length > 0 ? (
            <div>
              <p className="text-sm text-grey-800">
                <span className="font-semibold">{status.phone}</span> is on {status.carriers.length} account(s) without a Test Caller mapping:
              </p>
              <ul className="mt-1 text-xs text-grey-500">
                {status.carriers.map((c) => (
                  <li key={c.userId}>
                    {c.name || `user ${c.userId}`} (#{c.userId})
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-grey-400">
                Pick a caller type below — a tenant type moves the phone to the account you choose; Unknown caller wipes previous test identities and
                makes it a brand-new caller.
              </p>
            </div>
          ) : (
            <p className="text-sm text-grey-600">
              <span className="font-semibold">{status.phone}</span> is not attached to any account — the bot treats it as an unknown caller.
            </p>
          )}
        </div>
      )}

      {/* Caller type filter — locked until the phone has been set (checked) */}
      {!status && !isLoadingStatus && <p className="text-sm text-grey-400">Enter your phone number and check its status to pick a caller type.</p>}
      {status && (
        <div>
          <h3 className="mb-2 text-sm font-semibold text-grey-700">Pick a caller type</h3>
          <div className="flex flex-wrap gap-2">
            {TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.type}
                type="button"
                className={filterClasses(
                  "rounded-lg border px-3 py-2 text-left transition-colors",
                  selectedType === opt.type ? "border-orange-400 bg-orange-50" : "border-grey-200 bg-white hover:border-grey-300"
                )}
                onClick={() => void handleSelectType(opt.type)}
              >
                <span className="block text-sm font-medium text-grey-800">{opt.label}</span>
                <span className="block text-xs text-grey-500">{opt.description}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Candidates */}
      {!status ? null : isLoadingCandidates ? (
        <div className="flex h-24 items-center justify-center">
          <Spinner />
        </div>
      ) : candidates ? (
        candidates.length === 0 ? (
          <p className="text-sm text-grey-500">No accounts on this org match that caller type.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-grey-200 text-left text-xs text-grey-400">
                <th className="py-2 pr-2 font-medium">Account</th>
                <th className="py-2 pr-2 font-medium">Leases</th>
                <th className="py-2 pr-2 font-medium">Balance</th>
                <th className="py-2 pr-2 font-medium">Location(s)</th>
                <th className="py-2 pr-2 font-medium">Last bot activity</th>
                <th className="py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {candidates.map((c) => (
                <tr key={c.userId} className="border-b border-grey-100">
                  <td className="py-2 pr-2 text-grey-800">
                    {c.name || `user ${c.userId}`}
                    <span className="ml-1 text-xs text-grey-400">#{c.userId}</span>
                  </td>
                  <td className="py-2 pr-2 text-grey-600">{c.activeLeaseCount}</td>
                  <td className={filterClasses("py-2 pr-2", c.pastDue ? "font-medium text-red-600" : "text-grey-600")}>
                    ${c.totalBalance.toFixed(2)}
                    {c.pastDue && <span className="ml-1 text-xs">past due</span>}
                  </td>
                  <td className="py-2 pr-2 text-xs text-grey-500">{c.locationNames.join(", ") || "—"}</td>
                  <td className="py-2 pr-2 text-xs text-grey-500">{c.lastBotActivityAt ? new Date(c.lastBotActivityAt).toLocaleString() : "none"}</td>
                  <td className="py-2 text-right">
                    <Button
                      size={ButtonSize.SMALL}
                      variant={ButtonVariant.PRIMARY}
                      color={ButtonColor.ORANGE}
                      onClick={() => handleSelectCandidate(c)}
                      disabled={busyUserId !== null || !phone.trim()}
                    >
                      Select
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : null}

      {/* Confirm screen — become an unknown caller (reset) */}
      {pendingUnknown && (
        <div className="fixed inset-0 z-500 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true">
          <div className="w-[480px] rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-base font-semibold text-grey-800">Become an unknown caller?</h3>
            <p className="mt-3 text-sm text-grey-600">
              Your phone <span className="font-medium text-grey-700">{phone}</span> will be detached from every account on this org, and any previous
              test identities (bot-created users with no real account data) will be permanently deleted along with their conversations. The bot will
              treat your next call or text as a brand-new caller.
            </p>
            <p className="mt-2 text-xs text-grey-400">
              Real accounts are never deleted — they're only detached. Safe to run even if nothing is attached.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={() => setPendingUnknown(false)} disabled={isResetting}>
                Cancel
              </Button>
              <Button
                size={ButtonSize.SMALL}
                variant={ButtonVariant.PRIMARY}
                color={ButtonColor.ORANGE}
                onClick={() => void handleConfirmUnknown()}
                disabled={isResetting}
              >
                {isResetting ? <Spinner /> : "Become a new caller"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm screen — become this caller */}
      {pendingCandidate && (
        <div className="fixed inset-0 z-500 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true">
          <div className="w-[480px] rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-base font-semibold text-grey-800">Become this caller?</h3>

            <div className="mt-3 rounded-lg border border-grey-200 bg-grey-50 p-3 text-sm">
              <p className="font-medium text-grey-800">
                {pendingCandidate.name || `user ${pendingCandidate.userId}`}
                <span className="ml-1 text-xs font-normal text-grey-400">#{pendingCandidate.userId}</span>
              </p>
              <p className="mt-0.5 text-xs text-grey-500">
                {pendingCandidate.activeLeaseCount} active lease{pendingCandidate.activeLeaseCount === 1 ? "" : "s"} · $
                {pendingCandidate.totalBalance.toFixed(2)}
                {pendingCandidate.pastDue ? " past due" : ""} · {pendingCandidate.locationNames.join(", ") || "no location"}
              </p>
              <p className="mt-1 text-xs text-grey-500">
                Your phone <span className="font-medium text-grey-700">{phone}</span> will resolve to this account.
              </p>
            </div>

            <div className="mt-4 flex flex-col gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-grey-500" htmlFor="test-caller-confirm-name">
                  Appear as (name)
                </label>
                <input
                  id="test-caller-confirm-name"
                  type="text"
                  className="w-full rounded-md border border-grey-200 px-3 py-2 text-sm text-grey-700 placeholder:text-grey-300 focus:border-orange-300 focus:outline-none"
                  placeholder={pendingCandidate.name ? `${pendingCandidate.name} (account's)` : "account's name"}
                  value={overrideName}
                  onChange={(e) => setOverrideName(e.target.value)}
                />
                <div className="mt-1 flex gap-3 text-xs">
                  <button type="button" className="text-teal-600 hover:underline" onClick={() => setOverrideName(Member.displayName || "")}>
                    Use my name ({Member.displayName})
                  </button>
                  <button type="button" className="text-teal-600 hover:underline" onClick={() => setOverrideName("")}>
                    Use the account's name{pendingCandidate.name ? ` (${pendingCandidate.name})` : ""}
                  </button>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-grey-500" htmlFor="test-caller-confirm-email">
                  Appear as (email)
                </label>
                <input
                  id="test-caller-confirm-email"
                  type="email"
                  className="w-full rounded-md border border-grey-200 px-3 py-2 text-sm text-grey-700 placeholder:text-grey-300 focus:border-orange-300 focus:outline-none"
                  placeholder={pendingCandidate.email ? `${pendingCandidate.email} (account's)` : "account's email"}
                  value={overrideEmail}
                  onChange={(e) => setOverrideEmail(e.target.value)}
                />
                <div className="mt-1 flex gap-3 text-xs">
                  {Member.account?.email && (
                    <button type="button" className="text-teal-600 hover:underline" onClick={() => setOverrideEmail(Member.account?.email || "")}>
                      Use my email ({Member.account.email})
                    </button>
                  )}
                  <button type="button" className="text-teal-600 hover:underline" onClick={() => setOverrideEmail("")}>
                    Use the account's email{pendingCandidate.email ? ` (${pendingCandidate.email})` : ""}
                  </button>
                </div>
              </div>
              <p className="text-xs text-grey-400">
                An empty field keeps the account's real value. Whatever you set temporarily replaces the account's name/email and is restored on Undo
                or Reset.
              </p>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={() => setPendingCandidate(null)} disabled={busyUserId !== null}>
                Cancel
              </Button>
              <Button
                size={ButtonSize.SMALL}
                variant={ButtonVariant.PRIMARY}
                color={ButtonColor.ORANGE}
                onClick={() => void handleConfirmApply()}
                disabled={busyUserId !== null}
              >
                {busyUserId !== null ? <Spinner /> : "Become this caller"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
